import type { ReactNode } from "react";
import {
  CalendarClock,
  Cloud,
  FileText,
  Folder,
  Globe,
  KeyRound,
  Layers,
  MessageSquare,
  Monitor,
  Network,
  ShieldCheck,
  Timer,
  UserRound,
  type LucideIcon,
} from "lucide-react";
import { cn } from "../../../utils/utils";
import { getStatusColor } from "./utils";
import "./ResourceRequests.css";

export function RequestStatusStrip({ status }: { status: string }) {
  return <span aria-hidden="true" className={`request-card-status-strip ${getStatusColor(status)}`} />;
}

interface RequestCardHeaderProps {
  icon: ReactNode;
  title: ReactNode;
  status: ReactNode;
  subtitle: ReactNode;
  actions: ReactNode;
}

export function RequestCardHeader({
  icon,
  title,
  status,
  subtitle,
  actions,
}: RequestCardHeaderProps) {
  return (
    <header className="request-card-header">
      <span className="request-card-resource-icon" aria-hidden="true">
        {icon}
      </span>
      <div className="request-card-identity">
        <div className="request-card-title-row">
          <h3>{title}</h3>
          {status}
        </div>
        <div className="request-card-subtitle">{subtitle}</div>
      </div>
      <div className="request-card-header-actions">{actions}</div>
    </header>
  );
}

const fieldIcons: Record<string, LucideIcon> = {
  computer: Monitor,
  category: Layers,
  duration: Timer,
  privilege: ShieldCheck,
  "access type": ShieldCheck,
  "access name": KeyRound,
  policies: ShieldCheck,
  "machine user": UserRound,
  "machine username": UserRound,
  "connection username": UserRound,
  "connection via": Network,
  "account id": Cloud,
  "user name": UserRound,
  "azure username": UserRound,
  "gcp user email": UserRound,
  "role name": ShieldCheck,
  "role id": ShieldCheck,
  "resource group": Folder,
  "resource name": Folder,
  "project id": Cloud,
  "azure id": Cloud,
  cluster: Cloud,
  role: ShieldCheck,
  binding: Network,
  verbs: KeyRound,
  namespace: Layers,
  "resource type": Layers,
  "role kind": ShieldCheck,
  resource: Folder,
  "request id": FileText,
  status: ShieldCheck,
  "requested by": UserRound,
  "created at": CalendarClock,
  created: CalendarClock,
  "computer name": Monitor,
  "gcp username": UserRound,
  "aws account": Cloud,
  scope: Folder,
  "web url": Globe,
  "approved by": UserRound,
  "approved at": CalendarClock,
  "declined at": CalendarClock,
  "revoked by": UserRound,
  "revoked at": CalendarClock,
  url: Globe,
  credential: KeyRound,
};

export function RequestInfoGrid({
  children,
  context = false,
  className,
}: {
  children: ReactNode;
  context?: boolean;
  className?: string;
}) {
  return (
    <dl
      className={cn(
        "request-card-metadata",
        context ? "request-card-context" : "request-card-facts",
        className,
      )}
    >
      {children}
    </dl>
  );
}

export function RequestInfoItem({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  const Icon = fieldIcons[label.toLowerCase()];
  return (
    <div className="request-info-item">
      <dt>
        {Icon && <Icon aria-hidden="true" />}
        {label}
      </dt>
      <dd>{children}</dd>
    </div>
  );
}

export function RequestCardSection({
  variant,
  children,
}: {
  variant: "history" | "schedule";
  children: ReactNode;
}) {
  const Icon = variant === "schedule" ? CalendarClock : ShieldCheck;
  return (
    <section className={`request-card-section request-card-${variant}`}>
      <div className="request-card-section-title">
        <Icon aria-hidden="true" />
        {variant === "schedule" ? "Scheduled access" : "Admin actions"}
      </div>
      <div className="request-card-section-content">{children}</div>
    </section>
  );
}

export function RequestCardReason({ children }: { children: ReactNode }) {
  return (
    <div className="request-card-reason">
      <MessageSquare aria-hidden="true" />
      <span className="request-card-reason-label">Reason</span>
      <span className="request-card-reason-text">{children}</span>
    </div>
  );
}
